import { useSearchParams } from 'react-router-dom';
import { OnboardingQueueTools } from '../../components/hr/OnboardingReviewTools';
// pages/hr/OnboardingDashboardPage.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Alert,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
  Stack,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  LinearProgress,
  Avatar,
  Divider,
} from '@mui/joy';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';
import { ResponsiveTable } from '../../components/common/ResponsiveTable';
import {
  Plus as AddOutlined,
  DotsThreeVertical as MoreVertOutlined,
  ArrowsClockwise as RefreshOutlined,
  Eye as VisibilityOutlined,
  PaperPlaneTilt as SendOutlined,
  Prohibit as BlockOutlined,
  CheckCircle as CheckCircleOutlined,
  Clock as PendingOutlined,
  Hourglass as HourglassEmptyOutlined,
  XCircle as CancelOutlined,
} from '@phosphor-icons/react';
import {
  useOnboardingSessions,
  useOnboardingPackages,
  useResendOnboardingInvite,
  useRevokeOnboardingAccess,
  useApproveOnboarding,
} from '../../hooks/useHr';
import type { OnboardingSessionStatus, OnboardingSessionListItem } from '../../types/hr';
import { OnboardingSessionDetailDrawer } from '../../components/hr/OnboardingSessionDetailDrawer';
import { OnboardingPackageModal } from '../../components/hr/OnboardingPackageModal';
import toast from 'react-hot-toast';

const statusColors: Record<OnboardingSessionStatus, 'success' | 'warning' | 'danger' | 'neutral' | 'primary'> = {
  pending: 'neutral',
  in_progress: 'primary',
  awaiting_review: 'warning',
  completed: 'success',
  expired: 'danger',
  cancelled: 'danger',
};

const statusIcons: Record<OnboardingSessionStatus, React.ReactNode> = {
  pending: <PendingOutlined />,
  in_progress: <HourglassEmptyOutlined />,
  awaiting_review: <RefreshOutlined />,
  completed: <CheckCircleOutlined />,
  expired: <CancelOutlined />,
  cancelled: <CancelOutlined />,
};

const statusLabels: Record<OnboardingSessionStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  awaiting_review: 'Awaiting Review',
  completed: 'Completed',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

// Tab index -> the status the server filters on. `undefined` is "All Sessions".
const tabStatuses: (OnboardingSessionStatus | undefined)[] = [
  'awaiting_review',
  'in_progress',
  'pending',
  'completed',
  undefined,
];

const PAGE_SIZE = 20;

export const OnboardingDashboardPage: React.FC = () => {
  const [urlParams, setUrlParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [queue, setQueue] = useState('');
  const [owner, setOwner] = useState('');
  const [activeTab, setActiveTab] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(Boolean(urlParams.get('session')));
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(urlParams.get('session'));
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [editPackageId, setEditPackageId] = useState<string | null>(null);

  // The open tab drives the server query, so the list is never a client-side
  // slice of one page
  const { data, isLoading, error: loadError, refetch } = useOnboardingSessions({
    status: queue ? undefined : tabStatuses[activeTab],
    search, queue, owner,
    page: currentPage,
  });
  const sessions = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // The list request only carries the count for the tab that happens to be
  // open, so ask for each status separately with page_size=1 and read `count`.
  // Filtering the loaded page reported 0 whenever the backlog sat past row 20.
  const pendingCount = useOnboardingSessions({ status: 'pending', page_size: 1 }).data?.count ?? 0;
  const inProgressCount = useOnboardingSessions({ status: 'in_progress', page_size: 1 }).data?.count ?? 0;
  const awaitingReviewCount = useOnboardingSessions({ status: 'awaiting_review', page_size: 1 }).data?.count ?? 0;
  const completedCount = useOnboardingSessions({ status: 'completed', page_size: 1 }).data?.count ?? 0;

  const { data: packages = [] } = useOnboardingPackages();
  const resendInvite = useResendOnboardingInvite();
  const revokeAccess = useRevokeOnboardingAccess();
  const approveOnboarding = useApproveOnboarding();

  // Switching tabs switches the query - page 3 of the previous status is
  // meaningless against the new one
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, search, queue, owner]);

  useEffect(() => {
    const id = urlParams.get('session');
    setSelectedSessionId(id); setDetailDrawerOpen(Boolean(id));
  }, [urlParams]);

  const handleResendInvite = async (sessionId: string) => {
    try {
      await resendInvite.mutateAsync(sessionId);
      toast.success('Invitation resent successfully');
    } catch {
      toast.error('Failed to resend invitation');
    }
  };

  const handleRevokeAccess = async (sessionId: string) => {
    if (!confirm('Are you sure you want to revoke access for this onboarding session?')) {
      return;
    }
    try {
      await revokeAccess.mutateAsync({ id: sessionId, reason: 'Revoked by administrator' });
      toast.success('Access revoked successfully');
    } catch {
      toast.error('Failed to revoke access');
    }
  };

  const handleApprove = async (sessionId: string) => {
    try {
      await approveOnboarding.mutateAsync({ id: sessionId });
      toast.success('Onboarding approved! Team member is now active.');
    } catch {
      toast.error('Failed to approve onboarding');
    }
  };

  const handleViewDetails = (sessionId: string) => {
    setUrlParams(previous => { previous.set('session', sessionId); return previous; });
    setSelectedSessionId(sessionId);
    setDetailDrawerOpen(true);
  };

  const handleCreatePackage = () => {
    setEditPackageId(null);
    setPackageModalOpen(true);
  };

  const handleEditPackage = (packageId: string) => {
    setEditPackageId(packageId);
    setPackageModalOpen(true);
  };

  const renderSessionsTable = (sessionList: OnboardingSessionListItem[], showActions = true) => {
    if (loadError) return <Alert color="danger" endDecorator={<Button onClick={() => refetch()}>Retry</Button>}>Onboarding sessions could not be loaded.</Alert>;
    if (isLoading) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography level="body-sm" color="neutral">Loading…</Typography>
        </Box>
      );
    }
    if (sessionList.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography level="body-sm" color="neutral">
            No sessions in this category
          </Typography>
        </Box>
      );
    }
    return (
      <ResponsiveTable<OnboardingSessionListItem>
        rows={sessionList}
        getRowKey={(s) => s.id}
        columns={[
          {
            key: 'team_member',
            sortValue: (row) => row.team_member_name,
            header: 'Team Member',
            mobileVariant: 'header',
            render: (session) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Avatar size="sm">
                  {session.team_member_name.split(' ').map(n => n[0]).join('')}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography level="title-sm" sx={{ wordBreak: 'break-word' }}>
                    {session.team_member_name}
                  </Typography>
                  <Typography level="body-xs" color="neutral" sx={{ wordBreak: 'break-all' }}>
                    {session.team_member_email}
                  </Typography>
                </Box>
              </Box>
            ),
          },
          {
            key: 'package',
            sortValue: (row) => row.package_name,
            header: 'Package',
            render: (session) => (
              <Typography level="body-sm">{session.package_name || 'Default'}</Typography>
            ),
          },
          {
            key: 'progress',
            sortValue: (row) => row.completion_percentage,
            header: 'Progress',
            render: (session) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                <LinearProgress
                  determinate
                  value={session.completion_percentage}
                  sx={{ flex: 1, maxWidth: 100 }}
                />
                <Typography level="body-xs">
                  {session.completion_percentage}%
                </Typography>
              </Box>
            ),
          },
          {
            key: 'start_date', header: 'Expected start', render: row => row.start_date || 'Not set',
          },
          {
            key: 'status',
            sortValue: (row) => row.status,
            header: 'Status',
            render: (session) => (
              <Chip
                size="sm"
                color={statusColors[session.status]}
                variant="soft"
                startDecorator={statusIcons[session.status]}
              >
                {statusLabels[session.status]}
              </Chip>
            ),
          },
          {
            key: 'invited',
            header: 'Invited',
            render: (session) => (
              <Typography level="body-xs" color="neutral">
                {session.invitation_sent_at
                  ? new Date(session.invitation_sent_at).toLocaleDateString()
                  : 'Not sent'}
              </Typography>
            ),
          },
        ]}
        rowActions={
          showActions
            ? (session) => (
                <Dropdown>
                  <MenuButton
                    slots={{ root: IconButton }}
                    slotProps={{ root: { variant: 'plain', size: 'sm' } }}
                  >
                    <MoreVertOutlined />
                  </MenuButton>
                  <Menu placement="bottom-end">
                    <MenuItem onClick={() => handleViewDetails(session.id)}>
                      <VisibilityOutlined style={{ marginRight: 8 }} />
                      View Details
                    </MenuItem>
                    {session.status === 'awaiting_review' && (
                      <MenuItem onClick={() => handleApprove(session.id)}>
                        <CheckCircleOutlined style={{ marginRight: 8 }} />
                        Approve Onboarding
                      </MenuItem>
                    )}
                    {['pending', 'in_progress'].includes(session.status) && (
                      <MenuItem onClick={() => handleResendInvite(session.id)}>
                        <SendOutlined style={{ marginRight: 8 }} />
                        Resend Invitation
                      </MenuItem>
                    )}
                    {['pending', 'in_progress'].includes(session.status) && <Divider />}
                    {['pending', 'in_progress'].includes(session.status) && (
                      <MenuItem color="danger" onClick={() => handleRevokeAccess(session.id)}>
                        <BlockOutlined style={{ marginRight: 8 }} />
                        Revoke Access
                      </MenuItem>
                    )}
                  </Menu>
                </Dropdown>
              )
            : undefined
        }
      />
    );
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>Onboarding Dashboard</Typography>
            <PageHelpButton />
          </Box>
          <Typography level="body-sm" color="neutral">
            Review missing paperwork, follow up with new hires, and approve completed onboarding.
          </Typography>
        </Box>
        <Button startDecorator={<AddOutlined />} onClick={() => window.location.href = '/hr/team?invite=1'}>
          Invite New Hire
        </Button>
      </Stack>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography level="body-xs" color="neutral">Pending</Typography>
            <Typography level="h3">{pendingCount}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography level="body-xs" color="neutral">In Progress</Typography>
            <Typography level="h3">{inProgressCount}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography level="body-xs" color="neutral">Awaiting Review</Typography>
            <Typography level="h3" color="warning">{awaitingReviewCount}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography level="body-xs" color="neutral">Completed</Typography>
            <Typography level="h3" color="success">{completedCount}</Typography>
          </CardContent>
        </Card>
      </Box>

      <OnboardingQueueTools sessions={sessions} search={search} onSearch={setSearch} queue={queue} onQueue={value => { setQueue(value); setActiveTab(4); }} owner={owner} onOwner={setOwner} />
      {/* Tabs */}
      <Card>
        <Tabs value={activeTab} onChange={(_, value) => { setActiveTab(value as number); setQueue(''); }}>
          <TabList sx={{ flexWrap: 'wrap', '& > *': { flex: { xs: '1 1 30%', sm: '0 0 auto' } } }}>
            <Tab>
              Awaiting Review
              {awaitingReviewCount > 0 && (
                <Chip size="sm" color="warning" sx={{ ml: 1 }}>
                  {awaitingReviewCount}
                </Chip>
              )}
            </Tab>
            <Tab>
              In Progress
              {inProgressCount > 0 && (
                <Chip size="sm" color="primary" sx={{ ml: 1 }}>
                  {inProgressCount}
                </Chip>
              )}
            </Tab>
            <Tab>
              Pending
              {pendingCount > 0 && (
                <Chip size="sm" color="neutral" sx={{ ml: 1 }}>
                  {pendingCount}
                </Chip>
              )}
            </Tab>
            <Tab>Completed</Tab>
            <Tab>All Sessions</Tab>
          </TabList>

          {/* Every panel renders the current server page; the open tab is what
              decides which status was requested. */}
          <TabPanel value={0}>
            {renderSessionsTable(sessions)}
          </TabPanel>
          <TabPanel value={1}>
            {renderSessionsTable(sessions)}
          </TabPanel>
          <TabPanel value={2}>
            {renderSessionsTable(sessions)}
          </TabPanel>
          <TabPanel value={3}>
            {renderSessionsTable(sessions, false)}
          </TabPanel>
          <TabPanel value={4}>
            {renderSessionsTable(sessions)}
          </TabPanel>
        </Tabs>

        {/* Pagination */}
        {totalCount > 0 && (
          <Box
            sx={{
              px: 2,
              pb: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5,
            }}
          >
            <Typography level="body-sm" color="neutral">
              {`Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, totalCount)}–${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount} sessions`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(p => p - 1)}
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Card>

      {/* Packages Section */}
      <Box sx={{ mt: 4 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ mb: 2, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
        >
          <Typography level="h4">Onboarding Packages</Typography>
          <Button variant="outlined" color="primary" size="sm" onClick={handleCreatePackage}>
            Manage Packages
          </Button>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          {packages.map((pkg) => (
            <Card key={pkg.id} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => handleEditPackage(pkg.id)}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography level="title-md">{pkg.name}</Typography>
                    <Typography level="body-sm" color="neutral">
                      {pkg.applies_to === 'all' ? 'All team members' :
                        pkg.applies_to === 'w2' ? 'W-2 employees only' : '1099 contractors only'}
                    </Typography>
                  </Box>
                  {pkg.is_default && (
                    <Chip size="sm" color="primary" variant="soft">Default</Chip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Typography level="body-xs">
                    {pkg.step_count} steps
                  </Typography>
                  <Typography level="body-xs">
                    {pkg.template_count} documents
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
          {packages.length === 0 && (
            <Card variant="outlined" sx={{ gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 3' }, textAlign: 'center', py: 4 }}>
              <Typography level="body-sm" color="neutral">
                No onboarding packages created yet. Create your first package to streamline new hire onboarding.
              </Typography>
              <Button variant="soft" size="sm" sx={{ mt: 2 }} onClick={handleCreatePackage}>
                Create Package
              </Button>
            </Card>
          )}
        </Box>
      </Box>

      {/* Onboarding Session Detail Drawer */}
      <OnboardingSessionDetailDrawer
        open={detailDrawerOpen}
        onClose={() => { setDetailDrawerOpen(false); setUrlParams(previous => { previous.delete('session'); return previous; }); }}
        sessionId={selectedSessionId}
      />

      {/* Onboarding Package Modal */}
      <OnboardingPackageModal
        open={packageModalOpen}
        onClose={() => {
          setPackageModalOpen(false);
          setEditPackageId(null);
        }}
        packageId={editPackageId || undefined}
        onSuccess={() => toast.success(editPackageId ? 'Package updated' : 'Package created')}
      />
    </Box>
  );
};

export default OnboardingDashboardPage;
